/**
 * Session state, held in React context.
 *
 * Deliberately small: the token and the resolved API URL, nothing else. User
 * data that the server owns is fetched through TanStack Query like everything
 * else rather than being copied into a store at login and going stale.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { clearSession, getToken, storeSession } from '../api/client';
import type { GatewayUser } from '../api/auth';
import { clearRecentLeads } from '../layout/search/recentLeads';

interface AuthContextValue {
  token: string | null;
  user: GatewayUser | null;
  signIn: (token: string, apiUrl: string, user: GatewayUser) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_KEY = 'hawfinch_user';

/**
 * "Decide later" on the one-time sign-up template question, held for this browser
 * session. Cleared on every sign-in and sign-out so it asks again next time.
 */
export const VOCAB_CHOICE_LATER_KEY = 'hawfinch_vocab_choice_later';

function forgetSessionChoices() {
  try {
    sessionStorage.removeItem(VOCAB_CHOICE_LATER_KEY);
  } catch {
    /* private mode */
  }
}

function readStoredUser(): GatewayUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as GatewayUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<GatewayUser | null>(() => readStoredUser());

  const signIn = useCallback((newToken: string, apiUrl: string, newUser: GatewayUser) => {
    storeSession(newToken, apiUrl);
    forgetSessionChoices();
    // Another person's recently viewed records are not this one's.
    clearRecentLeads();
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    } catch {
      /* private mode */
    }
    setToken(newToken);
    setUser(newUser);
    // The generated client captured its baseUrl at module load. A full reload
    // is the honest way to repoint it at the tenant's server, and it happens
    // exactly once per login.
    window.location.reload();
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    forgetSessionChoices();
    clearRecentLeads();
    try {
      localStorage.removeItem(USER_KEY);
    } catch {
      /* private mode */
    }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, signIn, signOut }),
    [token, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * The session if there is one, or null outside the provider. For pieces that
 * also render where nobody is signed in (a dialog shown in a test, say) and
 * only need to know what to hide.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
